<template>
    <div class="edit-user client-user" id="edit-user">
        <!-- View Mode -->
        <div v-if="!isEditMode" class="user-profile-view position-relative">
            <button
                class="btn btn-secondary btn-icon-only-md position-absolute top-0 end-0 edit-profile-btn" style="z-index: 10;"
                @click="enterEditMode"
                data-bs-toggle="tooltip"
                title="Edit Profile"
            >
                <img src="/img/icons/avid-icons/edit.svg" alt="Edit Profile" class="icon">
            </button>
            <div class="d-flex align-items-start">
                <div class="profile-header">
                    <div class="profile-image-container">
                        <img 
                            :src="user.image || '/img/images/profile_image_placeholder.png'" 
                            :alt="`${user.first_name} ${user.last_name}`"
                            :key="user.image || 'placeholder'"
                            class="profile-image"
                        >
                        <button 
                            class="btn btn-secondary btn-icon-only-sm"
                            @click="openImageModal"
                            data-bs-toggle="tooltip"
                            title="Change Profile Image"
                        >
                           <img src="/img/icons/avid-icons/upload.svg" alt="Change Profile Image" class="icon">
                        </button>
                    </div>
                    <div class="profile-info">
                        <h3 class="mb-0">{{ user.first_name }} {{ user.last_name }}</h3>
                        <p class="mb-0">{{ displayOrganization }}</p>
                        <p class="mb-2">{{ user.email }}</p>
                        <p class="mb-0 d-flex align-items-center mt-1">
                            <img v-if="user.two_factor_enabled" src="/img/icons/avid-icons/check-circle.svg" class="icon me-1 text-success" alt="">
                            <img v-else src="/img/icons/avid-icons/red-remove.svg" class="icon me-1 text-danger" alt="">
                            <span :class="user.two_factor_enabled ? 'text-success' : 'text-danger'">
                                {{ user.two_factor_enabled ? '2FA Enabled' : '2FA Disabled' }}
                            </span>
                        </p>
                        <!-- Recovery codes (view only, after first enable or regenerate) -->
                        <div v-if="user.two_factor_enabled && twoFactorRecoveryCodes.length" class="mt-4">
                            <h6 class="mb-2">Two-Factor Recovery Codes</h6>
                            <p class="small text-muted mb-3">
                                Save these codes in a safe place. They will not be shown again after you reload this page. Each code can be used once if you lose access to your authenticator app.
                            </p>
                            <div class="font-monospace small mb-3 bg-secondary card p-3">
                                <div class="row g-2">
                                    <div v-for="(code, i) in twoFactorRecoveryCodes" :key="i" class="col-6 col-md-4">{{ code }}</div>
                                </div>
                            </div>
                            <div class="d-flex flex-wrap gap-2 justify-content-end">
                                <button type="button" class="btn btn-primary btn-sm" @click="downloadTwoFactorRecoveryCodes">
                                    Download
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit Mode -->
        <div v-else class="user-profile-edit">
            <div class="profile-header mb-4">
                <h1 class="mb-0">Account</h1>
                <div class="profile-info">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group">
                                    <input 
                                        type="text" 
                                        class="form-control profile-input" 
                                        v-model="user.first_name"
                                        placeholder="First Name"
                                     aria-label="Text input">
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group">
                                    <input 
                                        type="text" 
                                        class="form-control profile-input" 
                                        v-model="user.last_name"
                                        placeholder="Last Name"
                                     aria-label="Text input">
                                </div>
                            </div>
                        </div>
                        <div class="form-group">
                            <input 
                                type="email" 
                                class="form-control profile-input" 
                                :class="{ 'is-invalid': errors.email }"
                                v-model="user.email" 
                                placeholder="Email Address"
                             aria-label="Email address">
                            <div v-if="errors.email" class="text-danger small mt-1">
                                {{ errors.email }}
                            </div>
                        </div>

                        <!-- Two-Factor Authentication (only for this user's own account) -->
                        <div class="two-factor-section">
                            <div class="form-check form-switch d-inline-flex align-items-center gap-2 mb-3">
                                <input
                                    id="two-factor-toggle"
                                    class="form-check-input mt-0 me-2"
                                    :class="{ 'two-factor-toggle--enabled': desiredTwoFactorEnabled }"
                                    type="checkbox"
                                    role="switch"
                                    v-model="desiredTwoFactorEnabled"
                                 aria-label="Checkbox">
                                <label class="form-check-label mb-0" for="two-factor-toggle">
                                    {{ desiredTwoFactorEnabled ? 'Two-factor authentication enabled' : 'Two-factor authentication disabled' }}
                                </label>
                            </div>
                        </div>

                        <!-- Password Change Section -->
                        <div class="password-change-section">
                            <h6 class="text-muted mb-3">Change Password</h6>
                            
                            <div class="form-group mb-3">
                                <password-visibility
                                    :model-value="user.current_password"
                                    @update:modelValue="val => { user.current_password = val; validateCurrentPassword(); }"
                                    name="current_password"
                                    placeholder="Current Password"
                                    :required="false"
                                    autocomplete="current-password"
                                    :has-error="errors.current_password != ''"
                                    :show-inline-error="false"
                                />
                                <div v-if="errors.current_password" class="text-danger small mt-1">
                                    {{ errors.current_password }}
                                </div>
                            </div>
                            
                            <div class="form-group">
                                <password-visibility
                                    :model-value="user.new_password"
                                    @update:modelValue="val => { user.new_password = val; validateNewPassword(); }"
                                    name="new_password"
                                    placeholder="New Password"
                                    :required="false"
                                    autocomplete="new-password"
                                    :has-error="errors.new_password != ''"
                                    :show-inline-error="false"
                                    :disabled="!isCurrentPasswordValid || !user.current_password"
                                />
                                <div v-if="errors.new_password" class="text-danger small mt-1">
                                    {{ errors.new_password }}<br>
                                    <em class="small">(Password must be at least 8 characters long, complex enough to be hard to guess, and avoid common patterns and sequences)</em>
                                </div>
                            </div>
                        </div>
                </div>
            </div>
            <div class="text-end">
                <button class="btn btn-secondary me-2 px-4" @click="exitEditMode" data-bs-toggle="tooltip" title="Cancel">
                    Cancel
                </button>
                <button class="btn btn-primary px-4" @click="saveUser" data-bs-toggle="tooltip" title="Save Changes">
                    Save
                </button>
            </div>
        </div>

        <!-- Image Edit Modal -->
        <image-edit-modal
            :id="'image-edit-modal'"
            :current-image="user.image"
            @image-updated="handleImageUpdate"
            ref="imageEditModal"
        />

        <!-- Two-Factor Enable Modal -->
        <div class="modal fade" id="two-factor-enable-modal" data-bs-backdrop="static" tabindex="-1" aria-labelledby="two-factor-enable-modal-title" aria-hidden="true" ref="twoFactorEnableModal">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header border-bottom-0 pb-0">
                        <div class="w-100">
                            <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" aria-label="Close" @click="closeTwoFactorEnableModal">
                                <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt="">
                            </button>
                            <h3 id="two-factor-enable-modal-title" class="mt-2">Enable Two-Factor Authentication</h3>
                        </div>
                    </div>
                    <div class="modal-body">
                        <div v-if="twoFactorModal.loading" class="text-center py-4">
                            <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
                        </div>
                        <template v-else>
                            <p class="mb-1"><strong>Step 1:</strong> Scan this QR code with your authenticator app (e.g. Google Authenticator, Authy).</p>
                            <div class="mb-3" v-html="twoFactorModal.qrCodeSvg" style="max-width: 200px;"></div>
                            <div class="mb-1">
                                <p class="mb-0"><strong>Step 2:</strong> Enter the 6-digit code from your app to confirm</p>
                                <input
                                    v-model="twoFactorModal.confirmCode"
                                    type="text"
                                    inputmode="numeric"
                                    maxlength="6"
                                    class="form-control"
                                    placeholder="000000"
                                 aria-label="Confirm Code">
                                <div v-if="twoFactorModal.confirmError" class="text-danger small mt-1">{{ twoFactorModal.confirmError }}</div>
                            </div>
                        </template>
                    </div>
                    <div class="modal-footer border-top-0">
                        <button type="button" class="btn btn-secondary px-4" :disabled="twoFactorModal.confirming" @click="closeTwoFactorEnableModal">Cancel</button>
                        <button type="button" class="btn btn-primary px-4" :disabled="twoFactorModal.confirming || !twoFactorModal.confirmCode || twoFactorModal.loading" @click="confirmTwoFactorEnable">
                            <span v-if="twoFactorModal.confirming">Confirming...</span>
                            <span v-else>Confirm and Enable</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error Messages -->
        <div v-if="form.error" class="alert alert-danger mt-3">
            There was an error saving your information. Please try again.
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import ImageEditModal from '../../components/ImageEditModal.vue';
import PasswordVisibility from '../../PasswordVisibility.vue';
import { useNotifications } from '../../../composables/useNotifications';
import { useUserStore } from '../../../stores/user';

export default defineComponent({
    name: 'EditUser',
    components: {
        ImageEditModal,
        PasswordVisibility
    },
    setup() {
        const { showNotification } = useNotifications();
        const userStore = useUserStore();
        return {
            showNotification,
            userStore
        };
    },
    props: {
        user: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            isEditMode: false,
            form: {
                busy: false,
                error: false
            },
            errors: { 
                email: "",
                image: "",
                current_password: "",
                new_password: ""
            },
            originalUser: null,
            isCurrentPasswordValid: false,
            desiredTwoFactorEnabled: !!(this.user?.two_factor_enabled),
            twoFactorModal: {
                qrCodeSvg: '',
                confirmCode: '',
                confirmError: '',
                loading: false,
                confirming: false
            },
            twoFactorRecoveryCodes: [],
            twoFactorRegenerating: false,
            twoFactorModalInstance: null
        };
    },
    computed: {
        displayOrganization() {
            return this.user?.organization || this.client?.name || '';
        }
    },
    watch: {
        'user.two_factor_enabled'(val) {
            this.desiredTwoFactorEnabled = !!val;
        }
    },
    mounted() {
        // Store original user data for comparison
        this.originalUser = { ...this.user };
        // Initialize password fields
        this.user.current_password = '';
        this.user.new_password = '';
        this.desiredTwoFactorEnabled = !!this.user?.two_factor_enabled;
    },
    methods: {
        enterEditMode() {
            this.isEditMode = true;
            this.originalUser = { ...this.user };
        },
        exitEditMode() {
            if (this.originalUser) {
                Object.assign(this.user, {
                    first_name: this.originalUser.first_name,
                    last_name: this.originalUser.last_name,
                    email: this.originalUser.email,
                    image: this.originalUser.image,
                    two_factor_enabled: this.originalUser.two_factor_enabled
                });
                this.desiredTwoFactorEnabled = !!this.originalUser.two_factor_enabled;
            }
            this.user.current_password = '';
            this.user.new_password = '';
            this.isCurrentPasswordValid = false;
            this.errors.current_password = '';
            this.errors.new_password = '';
            this.isEditMode = false;
        },
        getCsrfToken() {
            return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        },
        openImageModal() {
            const modalElement = document.getElementById('image-edit-modal');
            if (modalElement) {
                const modal = new window.bootstrap.Modal(modalElement);
                modal.show();
            }
        },
        async fetchTwoFactorRecoveryCodes() {
            if (!this.user?.two_factor_enabled) return;
            try {
                const { data } = await window.axios.get('/user/two-factor-recovery-codes', {
                    headers: { 'Accept': 'application/json' }
                });
                this.twoFactorRecoveryCodes = data;
            } catch (_) {
                this.twoFactorRecoveryCodes = [];
            }
        },
        async regenerateTwoFactorRecoveryCodes() {
            this.twoFactorRegenerating = true;
            try {
                await window.axios.post('/user/two-factor-recovery-codes', {}, {
                    headers: { 'X-XSRF-TOKEN': this.getCsrfToken(), 'Accept': 'application/json' }
                });
                const { data } = await window.axios.get('/user/two-factor-recovery-codes', {
                    headers: { 'Accept': 'application/json' }
                });
                this.twoFactorRecoveryCodes = data;
            } catch (_) {
                this.showNotification({ text: 'Failed to regenerate recovery codes.', type: 'error' });
            } finally {
                this.twoFactorRegenerating = false;
            }
        },
        downloadTwoFactorRecoveryCodes() {
            const text = 'Two-Factor Authentication Recovery Codes\n\nSave these codes in a safe place. Each can be used once if you lose access to your authenticator app.\n\n' + this.twoFactorRecoveryCodes.join('\n');
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'avid-recovery-codes.txt';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        },
        dismissRecoveryCodes() {
            this.twoFactorRecoveryCodes = [];
        },
        openTwoFactorEnableModal() {
            const el = this.$refs.twoFactorEnableModal;
            if (el && !this.twoFactorModalInstance) {
                this.twoFactorModalInstance = new window.bootstrap.Modal(el, { backdrop: 'static' });
                el.addEventListener('hidden.bs.modal', this.onTwoFactorModalHidden);
            }
            if (this.twoFactorModalInstance) {
                this.twoFactorModalInstance.show();
            }
        },
        closeTwoFactorEnableModal() {
            if (this.twoFactorModalInstance) {
                this.twoFactorModalInstance.hide();
            }
        },
        onTwoFactorModalHidden() {
            this.desiredTwoFactorEnabled = false;
            window.axios.delete('/user/two-factor-authentication', {
                headers: { 'X-XSRF-TOKEN': this.getCsrfToken(), 'Accept': 'application/json' }
            }).catch(() => {});
            this.twoFactorModal.qrCodeSvg = '';
            this.twoFactorModal.confirmCode = '';
            this.twoFactorModal.confirmError = '';
            this.twoFactorModal.loading = false;
            this.twoFactorModal.confirming = false;
            this.form.busy = false;
        },
        async confirmTwoFactorEnable() {
            const code = this.twoFactorModal.confirmCode?.trim();
            if (!code || code.length !== 6) {
                this.twoFactorModal.confirmError = 'Please enter the 6-digit code.';
                return;
            }
            this.twoFactorModal.confirmError = '';
            this.twoFactorModal.confirming = true;
            try {
                await window.axios.post('/user/confirmed-two-factor-authentication', { code }, {
                    headers: { 'X-XSRF-TOKEN': this.getCsrfToken(), 'Accept': 'application/json' }
                });
                const { data } = await window.axios.get('/user/two-factor-recovery-codes', {
                    headers: { 'Accept': 'application/json' }
                });
                this.twoFactorRecoveryCodes = data;
                this.user.two_factor_enabled = true;
                const el = this.$refs.twoFactorEnableModal;
                if (el) {
                    el.removeEventListener('hidden.bs.modal', this.onTwoFactorModalHidden);
                }
                this.twoFactorModal.qrCodeSvg = '';
                this.twoFactorModal.confirmCode = '';
                this.twoFactorModal.confirmError = '';
                this.twoFactorModal.loading = false;
                this.twoFactorModal.confirming = false;
                this.form.busy = false;
                this.closeTwoFactorEnableModal();
                this.doProfileSave();
            } catch (e) {
                this.twoFactorModal.confirmError = e.response?.data?.message || e.response?.data?.errors?.code?.[0] || 'Invalid code. Please try again.';
            } finally {
                this.twoFactorModal.confirming = false;
            }
        },
        doProfileSave() {
            if (this.user.email !== this.originalUser?.email) {
                this.validateEmail();
            }
            const saveData = {
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                email: this.user.email,
                photo_url: this.user.image
            };
            if (this.user.current_password && this.user.new_password) {
                saveData.current_password = this.user.current_password;
                saveData.new_password = this.user.new_password;
            }
            window.axios.put(`/api/user/${this.user.id}`, saveData).then((response) => {
                this.form.busy = false;
                if (response?.data?.data?.image) {
                    this.user.image = response.data.data.image;
                }
                this.originalUser = { ...this.user };
                this.user.current_password = '';
                this.user.new_password = '';
                this.isCurrentPasswordValid = false;
                this.errors.current_password = '';
                this.errors.new_password = '';

                this.userStore.updateUser({
                    first_name: this.user.first_name,
                    last_name: this.user.last_name,
                    email: this.user.email,
                    image: this.user.image
                });

                this.showNotification({ text: 'Your profile has been saved!' });
                this.isEditMode = false;
            }).catch((error) => {
                this.form.error = true;
                this.form.busy = false;
                if (error.response?.data?.target === 'email') {
                    this.errors.email = error.response.data.message;
                } else if (error.response?.data?.target === 'current_password') {
                    this.errors.current_password = error.response.data.message;
                } else if (error.response?.data?.target === 'new_password') {
                    this.errors.new_password = error.response.data.message;
                }
                console.error('Save error:', error);
            });
        },
        async saveUser() {
            if (this.form.busy) return;
            if (this.user.current_password && !this.isCurrentPasswordValid) {
                this.errors.current_password = 'Please enter the correct current password';
                return;
            }
            if (this.user.new_password && this.user.current_password && !this.isCurrentPasswordValid) {
                this.errors.new_password = 'Current password must be verified before setting a new password';
                return;
            }
            this.form.busy = true;
            this.form.error = false;

            const pendingEnable = this.desiredTwoFactorEnabled && !this.user.two_factor_enabled;
            const pendingDisable = !this.desiredTwoFactorEnabled && this.user.two_factor_enabled;

            if (pendingDisable) {
                try {
                    await window.axios.delete('/user/two-factor-authentication', {
                        headers: { 'X-XSRF-TOKEN': this.getCsrfToken(), 'Accept': 'application/json' }
                    });
                    this.user.two_factor_enabled = false;
                    this.twoFactorRecoveryCodes = [];
                    this.desiredTwoFactorEnabled = false;
                } catch (e) {
                    this.form.busy = false;
                    this.showNotification({ text: e.response?.data?.message || 'Failed to disable two-factor authentication.', type: 'error' });
                    return;
                }
            }

            if (pendingEnable) {
                this.twoFactorModal.confirmCode = '';
                this.twoFactorModal.confirmError = '';
                this.twoFactorModal.loading = true;
                try {
                    await window.axios.post('/user/two-factor-authentication', {}, {
                        headers: { 'X-XSRF-TOKEN': this.getCsrfToken(), 'Accept': 'application/json' }
                    });
                    const { data } = await window.axios.get('/user/two-factor-qr-code', {
                        headers: { 'Accept': 'application/json' }
                    });
                    this.twoFactorModal.qrCodeSvg = data.svg;
                    this.twoFactorModal.loading = false;
                    this.openTwoFactorEnableModal();
                } catch (e) {
                    this.twoFactorModal.loading = false;
                    this.form.busy = false;
                    const msg = e.response?.data?.message || e.response?.data?.errors?.password?.[0] || 'Failed to enable two-factor authentication.';
                    this.showNotification({ text: msg, type: 'error' });
                }
                return;
            }

            this.doProfileSave();
        },
        validateEmail() {
            if (!this.user.email) return;
            
            window.axios.post('/api/user/check_email', { email: this.user.email, id: this.user.id })
                .then(response => {
                    if (response.status === 200) {
                        this.errors.email = response.data.exists 
                            ? 'The email entered is already attached to an existing user.'
                            : '';
                    }
                })
                .catch(error => {
                    console.error('Email validation error:', error);
                });
        },
        validateCurrentPassword() {
            if (!this.user.current_password) {
                this.errors.current_password = '';
                this.isCurrentPasswordValid = false;
                return;
            }

            // Validate current password with the server
            window.axios.post('/api/user/validate_current_password', {
                current_password: this.user.current_password
            })
            .then(response => {
                if (response.data.valid) {
                    this.errors.current_password = '';
                    this.isCurrentPasswordValid = true;
                } else {
                    this.errors.current_password = 'Current password is incorrect';
                    this.isCurrentPasswordValid = false;
                }
            })
            .catch(error => {
                this.errors.current_password = 'Error validating current password';
                this.isCurrentPasswordValid = false;
                console.error('Current password validation error:', error);
            });
        },
        validateNewPassword() {
            if (!this.user.new_password) {
                this.errors.new_password = '';
                return;
            }

            // Validate new password complexity using the API
            window.axios.post('/api/user/validate_password', {
                password: this.user.new_password
            })
            .then(response => {
                this.errors.new_password = response.data.valid ? '' : response.data.errors.join(' ');
            })
            .catch(error => {
                console.error('New password validation error:', error);
                this.errors.new_password = 'Error validating password. Please try again.';
            });
        },
        s3UploadSuccess(s3ObjectLocation) {
            // Fix Amazon URL encoding
            s3ObjectLocation = s3ObjectLocation.replace("%2F", "/");
            s3ObjectLocation = s3ObjectLocation.startsWith("http") ? s3ObjectLocation : 'https:'+ s3ObjectLocation;
            this.user.image = s3ObjectLocation;
        },
        handleImageUpdate(value) {
            // Update the user object reactively
            this.user.image = value;
            
            // Save the user information immediately when image is updated
            this.saveUserImage();
        },
        saveUserImage() {
            if (this.form.busy) return;
            
            this.form.busy = true;
            this.form.error = false;
            
            const saveData = {
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                email: this.user.email,
                photo_url: this.user.image
            };
            
            window.axios.put(`/api/user/${this.user.id}`, saveData).then((response) => {
                this.form.busy = false;
                if (response?.data?.data?.image) {
                    this.user.image = response.data.data.image;
                }
                this.originalUser = { ...this.user };

                this.userStore.updateUser({
                    image: this.user.image
                });
            })
            .catch(error => {
                this.form.error = true;
                this.form.busy = false;
                console.error('Save error:', error);
            });
        }
    }
});
</script>